import { useEffect, useState } from "react";
import clsx from "clsx";
import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { DictionaryEntity, DictionaryResultDict } from "../common/dictionary";
import { StyledPanel, StyledVerticalFlex } from "./Styled";
import { ImportMetaEnv } from "../common/types";
import { SearchInput } from "./SearchInput";

const API_URL = (import.meta as ImportMetaEnv).env.VITE_API_URL as string;
console.log("API_URL", API_URL); // eslint-disable-line no-console

const StyledSearchPage = styled.div(css`
  padding: 0rem;
  font-size: 1.5rem;
`);

const StyledResultDiv = styled.div(
  () => css`
    border: 1px solid #ccc;
    border-radius: 6px;
    padding: 0 1rem;
    margin: 1rem 0;

    h3 {
      color: #000;

      &.red {
        color: #dc3333;
      }
    }

    ul {
      padding-left: 0;
    }

    li {
      list-style-type: none;
    }
  `
);

interface SearchResultWordProps {
  word: string;
  entities: DictionaryEntity[];
}

interface ModelResult {
  id: string;
  score: number;
}

interface SearchResult {
  dictionaryResults: DictionaryResultDict[];
  modelResults: ModelResult[];
  explText: string;
}

const StyledModelResultPanel = styled.div(css`
  margin-top: 1rem;

  ul {
    li {
      list-style-type: none;
    }
    padding-left: 0;
  }
`);

interface ModelResultPanelProps {
  modelResults: ModelResult[];
}

const round2 = (num: number) => Math.round(num * 100) / 100;

const ModelResultPanel: React.FC<ModelResultPanelProps> = ({ modelResults }) => {
  return (
    <StyledModelResultPanel>
      <h2>Model Results</h2>
      <ul>
        {modelResults.map((model) => (
          <li key={model.id}>
            <b>{model.id}</b> {round2(100 * model.score)}
          </li>
        ))}
      </ul>
    </StyledModelResultPanel>
  );
};

const DictionaryEntityCard: React.FC<{ entity: DictionaryEntity }> = ({ entity }) => {
  if (!entity.aliases) {
    console.log("No aliases for", entity.title); // eslint-disable-line no-console
  }
  const aliases = entity.aliases && entity.aliases.length > 0 ? entity.aliases.join(", ") : "";
  const { id } = entity;
  let url = null;
  if (id.startsWith("/m") || id.startsWith("/g")) {
    url = `https://www.google.com/search?kgmid=${id}`;
  } else if (id.startsWith("lnr") || id.startsWith("xnr")) {
    url = `https://svenska.se/saol/?hv=${id}`;
  }
  return (
    <>
      <strong>{entity.title}</strong> | {url ? <a href={url}>{id}</a> : id}
      {aliases ? ` | ${aliases}` : ""} | {entity.subtitle ? entity.subtitle : ""}
    </>
  );
};

const SearchResultWord: React.FC<SearchResultWordProps> = ({ word, entities }) => {
  // let stemForm = lemmaToStemForm(entities);
  // if (stemForm === word) {
  //   stemForm = null;
  // }
  const stemForm = null;
  return (
    <StyledResultDiv>
      <h3 className={clsx({ red: entities.length === 0 })}>
        {word}
        {stemForm ? ` (${stemForm})` : ""}
      </h3>
      <ul>
        {entities.map((entity) => (
          <li key={entity.id}>
            <DictionaryEntityCard key={entity.id} entity={entity} />
          </li>
        ))}
      </ul>
    </StyledResultDiv>
  );
};

export function SearchPage() {
  const [searchValue, setSearchValue] = useState(() => localStorage.getItem("search-value") || "");
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);

  useEffect(() => {
    localStorage.setItem("search-value", searchValue);
  }, [searchValue]);

  useEffect(() => {
    if (!searchValue.trim()) {
      setSearchResult(null);
      return;
    }
    const url = `${API_URL}/api/search`;
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ clue: searchValue }),
    })
      .then((res) => res.json())
      .then((data) => {
        setSearchResult(data);
      })
      .catch((error) => console.error("Error fetching search results:", error)); // eslint-disable-line no-console
  }, [searchValue]);

  console.log("Search value:", searchValue); // eslint-disable-line no-console
  console.log("Search result:", searchResult); // eslint-disable-line no-console
  const explText = searchResult?.explText;
  if (explText) {
    console.log(explText); // eslint-disable-line no-console
  }

  return (
    <StyledSearchPage>
      <h1>Sök</h1>
      <StyledVerticalFlex>
        <StyledPanel>
          <SearchInput
            delay={1000}
            onChange={(value) => {
              setSearchValue(value);
            }}
            initialValue={searchValue}
          />
        </StyledPanel>

        {/* Render the search results if available */}
        {searchResult && searchResult.dictionaryResults && (
          <div style={{ marginTop: "0.5rem" }}>
            {searchResult.dictionaryResults.length === 0 ? (
              <p>No results found.</p>
            ) : (
              searchResult.dictionaryResults.map((resultGroup, groupIndex) => (
                <div key={groupIndex}>
                  {Object.keys(resultGroup).map((word) => (
                    <SearchResultWord key={word} word={word} entities={Object.values(resultGroup[word])} />
                  ))}
                </div>
              ))
            )}
          </div>
        )}
        {searchResult && searchResult.modelResults && searchResult.modelResults.length > 0 && (
          <ModelResultPanel modelResults={searchResult.modelResults} />
        )}
      </StyledVerticalFlex>
    </StyledSearchPage>
  );
}
